<template>
  <div class="alphafold-viewer">
    <div v-if="loading" class="d-flex justify-content-center py-4">
      <b-spinner label="Loading AlphaFold structure..."></b-spinner>
    </div>
    <div v-else-if="error" class="text-muted py-2">
      {{ errorMessage }}
    </div>
    <div v-else-if="bcifUrl">
      <div
        ref="viewerContainer"
        class="viewer-container"
        :style="{ height: viewerHeight + 'px' }"
      ></div>
      <div class="mt-2 d-flex justify-content-between align-items-center">
        <div class="confidence-legend d-flex align-items-center flex-wrap">
          <small class="mr-2 text-muted">Model Confidence:</small>
          <span class="legend-item">
            <span class="legend-color" style="background: #0053D6;"></span>
            <small>Very high (pLDDT &gt; 90)</small>
          </span>
          <span class="legend-item">
            <span class="legend-color" style="background: #65CBF3;"></span>
            <small>Confident (90 &gt; pLDDT &gt; 70)</small>
          </span>
          <span class="legend-item">
            <span class="legend-color" style="background: #FFDB13;"></span>
            <small>Low (70 &gt; pLDDT &gt; 50)</small>
          </span>
          <span class="legend-item">
            <span class="legend-color" style="background: #FF7D45;"></span>
            <small>Very low (pLDDT &lt; 50)</small>
          </span>
        </div>
        <a
          :href="alphafoldPageUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="ml-2"
        >View on AlphaFold</a>
      </div>
      <div class="mt-2">
        <small class="text-muted">
          Protein structure predicted by <a href="https://alphafold.ebi.ac.uk/" target="_blank" rel="noopener noreferrer"><strong>AlphaFold</strong></a> (Jumper et al., 2021).
          Data retrieved via the <a href="https://www.uniprot.org/" target="_blank" rel="noopener noreferrer"><strong>UniProt API</strong></a>.
          Visualized with <a href="https://molstar.org/" target="_blank" rel="noopener noreferrer"><strong>Mol*</strong></a> (Sehnal et al., 2021).
          Structure data licensed under <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer"><strong>CC-BY-4.0</strong></a>.
        </small>
      </div>
    </div>
  </div>
</template>

<script>
const PDBE_MOLSTAR_CSS =
  "https://cdn.jsdelivr.net/npm/pdbe-molstar@3.3.0/build/pdbe-molstar-light.css";
const PDBE_MOLSTAR_JS =
  "https://cdn.jsdelivr.net/npm/pdbe-molstar@3.3.0/build/pdbe-molstar-plugin.js";

let molstarLoaded = false;

function loadMolstar() {
  if (molstarLoaded) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.type = "text/css";
    link.href = PDBE_MOLSTAR_CSS;
    document.head.appendChild(link);

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = PDBE_MOLSTAR_JS;
    script.onload = () => {
      molstarLoaded = true;
      resolve();
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default {
  name: "AlphaFoldViewer",
  props: {
    uniprotId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      error: false,
      errorMessage: "We cannot retrieve an AlphaFold structure for this protein.",
      bcifUrl: null,
      cifUrl: null,
      entryId: null,
      viewerHeight: 450,
      viewerInstance: null,
    };
  },
  computed: {
    alphafoldPageUrl() {
      return this.entryId
        ? `https://alphafold.ebi.ac.uk/entry/${this.entryId}`
        : `https://alphafold.ebi.ac.uk/entry/AF-${this.uniprotId}-F1`;
    },
  },
  async mounted() {
    try {
      const response = await fetch(
        `https://alphafold.ebi.ac.uk/api/prediction/${this.uniprotId}`
      );
      if (!response.ok) {
        this.error = true;
        this.loading = false;
        return;
      }
      const data = await response.json();
      if (!data || data.length === 0) {
        this.error = true;
        this.loading = false;
        return;
      }
      const prediction = data[0];
      this.bcifUrl = prediction.bcifUrl;
      this.cifUrl = prediction.cifUrl;
      this.entryId = prediction.entryId;

      await loadMolstar();
      this.loading = false;

      // Wait for the DOM to update with the viewer container
      this.$nextTick(() => {
        this.initViewer();
      });
    } catch (err) {
      console.error("Error loading AlphaFold structure:", err);
      this.error = true;
      this.errorMessage = "Failed to load AlphaFold structure.";
      this.loading = false;
    }
  },
  beforeDestroy() {
    if (this.viewerInstance) {
      this.viewerInstance.clear();
    }
  },
  methods: {
    initViewer() {
      const container = this.$refs.viewerContainer;
      if (!container || !window.PDBeMolstarPlugin) return;

      const viewerInstance = new window.PDBeMolstarPlugin();
      this.viewerInstance = viewerInstance;

      const options = {
        customData: {
          url: this.cifUrl,
          format: "cif",
        },
        alphafoldView: true,
        bgColor: { r: 255, g: 255, b: 255 },
        hideControls: true,
        hideCanvasControls: ["expand", "animation"],
        sequencePanel: false,
        landscape: true,
      };

      viewerInstance.render(container, options);
    },
  },
};
</script>

<style scoped>
.viewer-container {
  width: 100%;
  position: relative;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}
.confidence-legend {
  gap: 8px;
}
.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.legend-color {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
}
</style>
